import { Kb3dModels } from '@models/classes/decorators/model-utils';
import { Token } from '@models/classes/token';

export type ITrackedClass<T = any> = T & {
    isModified: boolean;
    /** properties that have been changed since the last render processing */
    changes: Map<Extract<keyof T, string>, any>;
    /** the values of properties before they were changed */
    originalValues: Map<keyof T, any>;
    destroy: () => void;
    removedFromChildArray: (parent: ITrackedClass, item: any, arrayPropertyName: string) => void;
    addedToChildArray: (parent: ITrackedClass, item: any, arrayPropertyName: string) => void;
    commitChanges: () => void;
    setPropertyIsModified: (prop: string | symbol, modified: boolean) => void;
    trackChanges: (active: boolean) => void;
    nodeReferences:
        | Map<
              string | symbol,
              {
                  classRef: new (...args: any[]) => any;
                  limitKeys: Set<string | symbol> | undefined;
              }
          >
        | undefined;
    rawNode: T;
};

export type ITrackedArray<T> = T[] & {
    pop: (destroy: boolean) => T | undefined;
};

export enum EChangeOperation {
    // CreateAndPush and Remove correspond to functions in the custom KBArray class.
    // They use Push and Splice internally but also signify we need a
    // changeTracker destroy on undo and redo respectively and create on redo and undo respectively.
    Push,
    Pop,
    Shift,
    Unshift,
    Splice,
    CreateAndPush,
    Remove,
}

export interface IHistoryStateChange<T = any, G = any> {
    node: G & ITrackedClass;
    properties: Array<IHistoryStateChangeProperty<T, G>>;
}
export interface IHistoryStateChangeProperty<T = any, G = any> {
    name: keyof G;
    operation?: EChangeOperation;
    previousValue: T;
    newValue: T;
}

export function isTrackedClass<T>(object: T): object is ITrackedClass<T> {
    return object && typeof object === 'object' && '$changesTracked' in object;
}

export function isTrackedArray<T>(object: T[]): object is ITrackedArray<T> {
    return !!(object as any).$isTracked;
}

export function isTrackedInstance<T>(instance: T, token: Token<T>): instance is ITrackedClass<T> {
    if (!instance) return false;

    const model = Kb3dModels.get(token);
    return model ? instance instanceof model.classConstructor && isTrackedClass(instance) : false;
}

export function isTrackedInstanceOfAny<T>(instance: T, ...tokens: Token[]): instance is T {
    if (!instance) return false;

    return tokens.some(t => isTrackedInstance(instance, t));
}
